import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';

const fmt = (n) => 'UGX ' + parseFloat(n || 0).toLocaleString('en-UG', { minimumFractionDigits: 0 });
const num = (n) => parseInt(n || 0).toLocaleString();

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function Dashboard() {
  const [stats, setStats] = useState({});
  const [user, setUser] = useState(null);
  const [showGreeting, setShowGreeting] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    api.get('/api/reports/summary', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setStats(res.data))
      .catch(err => console.log('Dashboard stats error:', err.message));

    // User details (full_name, member_number) are saved at login — see
    // auth.js, which now joins the members table so this is available
    // without a separate API call here.
    try {
      const stored = localStorage.getItem('user');
      if (stored) setUser(JSON.parse(stored));
    } catch (e) { /* malformed or missing — greeting just falls back to username */ }

    // Dismissal is per-tab-session only (sessionStorage), so the greeting
    // reappears on the next real login rather than being hidden forever.
    if (sessionStorage.getItem('kams_greeting_dismissed') === '1') {
      setShowGreeting(false);
    }
  }, []);

  const dismissGreeting = () => {
    setShowGreeting(false);
    sessionStorage.setItem('kams_greeting_dismissed', '1');
  };

  // Staff land here (Dashboard is StaffRoute-only), and several officers
  // — Secretary, Treasurer — also hold their own personal member account.
  // Greeting them by personal name on the staff dashboard reads as if
  // they're in their Member Portal, not their office view. Greet by
  // role instead; falls back to the raw role string for anything not
  // explicitly labelled here (keeps working if a new role is added).
  const ROLE_WELCOME_LABELS = {
    admin: 'Administrator',
    finance: 'Treasurer',
    secretary: 'Secretary',
    chairman: 'Chairman',
    credit_officer: 'Credit Officer',
  };
  const displayRole = ROLE_WELCOME_LABELS[user?.role] || user?.role || 'there';
  // Role label alone ("our Treasurer") reads a bit stiff — pair it with
  // their given name when we have one, e.g. "our Treasurer Agnes". Member
  // records here are entered SURNAME-first (Ugandan convention, e.g.
  // "NATUKUNDA AGNES"), so the name people are actually called by is the
  // LAST word, not the first.
  const firstName = user?.full_name ? user.full_name.trim().split(/\s+/).pop() : '';

  // MIFL cards show COUNT of members + TOTAL amount, same "two-line"
  // shape as the other cards' label/value, packed into one card via
  // a small subtitle line rather than a separate card each — keeps
  // the row from growing to 7 cards. "Credit" is worded in the label
  // deliberately: the association owes members, shown as a positive
  // number, not a negative one — mifl.balance's sign is an internal
  // storage detail, never surfaced to the user.
  const cards = [
    { label: 'Total Members',    value: num(stats.total_members), color: '#3498db' },
    { label: 'Total Loans',      value: num(stats.total_loans),   color: '#2ecc71' },
    { label: 'Total Fines',      value: num(stats.total_fines),   color: '#e74c3c' },
    { label: 'Unpaid Fines',     value: fmt(stats.unpaid_fines),  color: '#e67e22' },
    { label: 'Active Loans',     value: fmt(stats.active_loans),  color: '#9b59b6' },
    {
      label: 'MIFL Arrears',
      value: fmt(stats.mifl_arrears_total),
      subtitle: `${num(stats.mifl_arrears_count)} member${stats.mifl_arrears_count === 1 ? '' : 's'}`,
      color: '#c0392b',
    },
    {
      label: 'MIFL Credit',
      value: fmt(stats.mifl_credit_total),
      subtitle: `${num(stats.mifl_credit_count)} member${stats.mifl_credit_count === 1 ? '' : 's'}`,
      color: '#16a085',
    },
  ];

  return (
    <div style={{ padding: '30px' }}>

      {showGreeting && user && (
        <div style={{
          background: '#2c3e50', color: 'white', padding: '18px 24px',
          borderRadius: '10px', marginBottom: '24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: '0 2px 10px rgba(44,62,80,0.15)'
        }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {getGreeting()}, our {displayRole}{firstName && ` ${firstName}`}
              {user.member_number && (
                <span style={{ fontWeight: 'normal', opacity: 0.7, fontSize: '13px', marginLeft: '10px' }}>
                  Member No: {user.member_number}
                </span>
              )}
            </div>
            <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '4px' }}>
              Here's how things stand with the association today.
            </div>
          </div>
          <button
            onClick={dismissGreeting}
            aria-label="Dismiss welcome message"
            style={{
              background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white',
              width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer',
              fontSize: '14px', lineHeight: 1
            }}
          >
            &times;
          </button>
        </div>
      )}

      <h2 style={{ color: '#2c3e50', marginBottom: '20px' }}>Dashboard</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        {cards.map((card, i) => (
          <div key={i} style={{
            background: card.color, color: 'white',
            padding: '20px', borderRadius: '8px',
            minWidth: '180px', flex: '1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>{card.label}</div>
            <div style={{ fontSize: '22px', fontWeight: 'bold', marginTop: '8px' }}>
              {card.value}
            </div>
            {card.subtitle && (
              <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '4px' }}>
                {card.subtitle}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Dashboard;